import { CAMERA_ALPHA } from '../game/camera';
import type { EntitySnapshot, TileKind, ZonePayload } from '../net/protocol';

/**
 * The minimap's arithmetic: where a world point lands on the map, and what a tile is
 * worth drawing as.
 *
 * Pure and separate from the component for the reason `drag.ts` and `vault.ts` are: the
 * projection is the part that can be *wrong*, and a wrong projection does not throw — it
 * draws a monster in the wrong room, which looks like a game bug rather than a maths one.
 * jsdom computes no layout and gives no canvas, so a projection living inside a paint
 * callback would be untestable in either suite.
 *
 * ## What the minimap is allowed to know
 *
 * **Only what the client already has**, which is the whole reason this needed no server
 * work: the `ZONE` frame carries every tile, and the 30 Hz snapshot carries every entity
 * the server has decided this player can see. What that is has been a *sight* radius since
 * 2026-08-08 rather than the pack activation range, so dormant packs do appear — standing
 * still, from about as far as they can be seen on screen. The map applies no filtering of
 * its own: hiding a monster that is visibly on screen would be worse than showing one that
 * is not.
 *
 * ## Which way is up
 *
 * **The map is rotated into screen space, not world space.** The camera is isometric at a
 * yaw of `-π/4`, so the world's `+x` axis runs diagonally down-right on screen — and an
 * axis-aligned map drew a player walking left as moving up-and-left, against a screen
 * that showed them going left. Reported directly: *going left should go left in the
 * minimap.*
 *
 * So every point is turned by the camera's yaw before it is scaled, and the four compass
 * directions on the map are the four directions on screen. The rotation comes from
 * `game/camera.ts` rather than a literal here, because a copied yaw is a second answer to
 * *which way is forward* and would drift silently the moment the camera moved.
 */

/** A minimap's pixel box. Square is not assumed: a zone need not be. */
export interface MapBox {
  width: number;
  height: number;
}

/** Where something sits on the map, in minimap pixels from its top-left. */
export interface MapPoint {
  x: number;
  y: number;
}

/**
 * The scale that fits a zone into a box, in minimap pixels per world unit.
 *
 * **One scale for both axes**, taken from the tighter fit. Scaling each axis
 * independently would fill the box but stretch the map, and a corridor that is square in
 * the world would read as a long hall — which is exactly the information a minimap exists
 * to give. A letterboxed map that is the right shape beats a full one that lies.
 */
export function mapScale(zone: ZonePayload, box: MapBox): number {
  // **Checked against the zone's own dimensions, not the rotated extent.** Rotation mixes
  // the two axes, so a zone of zero width still has a non-zero rotated bounding box — the
  // degenerate case would sail past a guard placed after the turn and produce a finite
  // scale for a map with no area.
  if (zone.width * zone.tileSize <= 0 || zone.height * zone.tileSize <= 0) return 0;

  const { width, height } = rotatedExtent(zone);
  return Math.min(box.width / width, box.height / height);
}

/**
 * The bounding box of the zone **after** it is turned into screen space.
 *
 * A rectangle rotated 45° needs a box `√2` times its own side, so fitting the unrotated
 * extent would push the corners of every zone off the map — and a corner is where the exit
 * usually is. The standard AABB of a rotated rectangle: each output side is the sum of the
 * two input sides projected onto it.
 */
function rotatedExtent(zone: ZonePayload): { width: number; height: number } {
  const w = zone.width * zone.tileSize;
  const h = zone.height * zone.tileSize;
  const c = Math.abs(Math.cos(CAMERA_ALPHA));
  const s = Math.abs(Math.sin(CAMERA_ALPHA));
  return { width: w * c + h * s, height: w * s + h * c };
}

/**
 * A world point turned into the map's screen-aligned frame, before scaling.
 *
 * World coordinates are already centred on the origin, so this is a plain rotation about
 * it — no translate-rotate-translate. The two axes are the camera's own:
 *
 * - `u` runs along screen-right, the world direction `(-sin α, cos α)`
 * - `v` runs *down* the screen, so it is the negative of screen-up
 *
 * At the shipped `α = -π/4` that makes `u = (x + z)/√2` and `v = (x − z)/√2`.
 */
function toScreenFrame(world: { x: number; z: number }): { u: number; v: number } {
  const sin = Math.sin(CAMERA_ALPHA);
  const cos = Math.cos(CAMERA_ALPHA);
  return {
    u: -world.x * sin + world.z * cos,
    v: world.x * cos + world.z * sin,
  };
}

/**
 * A world point in minimap pixels.
 *
 * Rotated into screen space first, then scaled, then centred in the box. **World `+z` is
 * not the map's `+y`** and has not been since the rotation landed — the camera looks along
 * a diagonal, so both world axes contribute to both map axes.
 *
 * Points outside the zone are projected rather than clamped: a caller that wants them
 * hidden should test for it, and a caller drawing an off-map arrow needs the real value.
 */
export function projectToMap(
  zone: ZonePayload,
  box: MapBox,
  world: { x: number; z: number },
): MapPoint {
  const scale = mapScale(zone, box);
  const { u, v } = toScreenFrame(world);

  // The rotated extent is centred on the origin too, so half of it is the shift that puts
  // the map's own centre in the middle of the box.
  return {
    x: box.width / 2 + u * scale,
    y: box.height / 2 + v * scale,
  };
}

/**
 * The four corners of one tile, projected — the shape the terrain layer paints.
 *
 * A rotated map cannot be painted with axis-aligned `fillRect`s: at 45° a grid of upright
 * squares drawn at rotated centres leaves diagonal gaps between every pair, and the floor
 * reads as hatching. Projecting the corners through **the same function the blips use**
 * keeps one answer to where a tile is, which is the point — a second projection for the
 * canvas would be free to drift from the one the monsters are drawn with, and the symptom
 * would be monsters standing slightly off the floor they are on.
 */
export function tileQuad(zone: ZonePayload, box: MapBox, tile: { col: number; row: number }): MapPoint[] {
  const halfW = (zone.width * zone.tileSize) / 2;
  const halfH = (zone.height * zone.tileSize) / 2;
  return [
    [0, 0],
    [1, 0],
    [1, 1],
    [0, 1],
  ].map(([dx, dz]) =>
    projectToMap(zone, box, {
      x: (tile.col + dx!) * zone.tileSize - halfW,
      z: (tile.row + dz!) * zone.tileSize - halfH,
    }),
  );
}

/** Whether a projected point is inside the box at all. */
export function onMap(box: MapBox, at: MapPoint): boolean {
  return at.x >= 0 && at.x <= box.width && at.y >= 0 && at.y <= box.height;
}

/**
 * What a tile is drawn as.
 *
 * Three classes, not eleven. A minimap read at a glance while something is hitting you
 * has room for *floor*, *wall* and *nothing*, and painting eleven tile kinds in eleven
 * colours would be a legend to learn rather than a map to read. The named places — entry,
 * exit, portals, the player, monsters — are drawn as **blips over** this, where they can
 * be given a colour that means something.
 *
 * `null` means paint nothing at all, which is what makes the zone's outline legible: void
 * is the absence of map, not a dark floor.
 */
export type TileClass = 'floor' | 'solid' | null;

export function tileClass(kind: TileKind): TileClass {
  switch (kind) {
    case 'VOID':
      return null;
    case 'BUILDING':
    case 'RAMPART':
    case 'OBSTACLE':
      return 'solid';
    // A `POOL` is not walkable, but it is *map* — bog water, lava or void mist still
    // tells you the shape of the room. Drawn as floor rather than as solid, because
    // reading it as a wall would make a lake look like a building.
    default:
      return 'floor';
  }
}

/** What a blip on the map means, which is what decides its colour. */
export type BlipKind = 'self' | 'player' | 'enemy' | 'elite' | 'entry' | 'exit' | 'portal';

export interface Blip {
  id: string;
  kind: BlipKind;
  at: MapPoint;
}

/**
 * Every entity worth a blip, projected.
 *
 * **Dead things are dropped and the rest are not filtered.** What the player may see is
 * the server's decision and it has already been made: a dormant pack is not in the
 * snapshot at all. Re-filtering here by distance or line of sight would be the client
 * inventing a fog of war the simulation does not have, and it would hide monsters that
 * are visibly on screen.
 *
 * A champion is its own kind rather than a bigger enemy dot, because "there is an elite
 * in that room" is a decision and "there are four monsters" is not. `ELITE` and above,
 * so a `MAGIC` mob does not raise an alarm it has not earned.
 */
export function entityBlips(
  zone: ZonePayload,
  box: MapBox,
  entities: readonly EntitySnapshot[],
  selfId: string | null,
): Blip[] {
  const out: Blip[] = [];
  for (const e of entities) {
    // A corpse is not a threat and not a landmark. The server keeps dead *players* in
    // the snapshot so the client can hold their mesh and show the rebirth overlay, so
    // this is a real case rather than a defensive one.
    if (e.hp <= 0) continue;
    const kind: BlipKind =
      e.id === selfId
        ? 'self'
        : e.kind === 'player'
          ? 'player'
          : e.rank !== undefined && e.rank !== 'NORMAL' && e.rank !== 'MAGIC'
            ? 'elite'
            : 'enemy';
    out.push({ id: e.id, kind, at: projectToMap(zone, box, { x: e.x, z: e.z }) });
  }
  return out;
}

/**
 * The zone's fixed landmarks: where you came in and where you go on.
 *
 * Separate from `entityBlips` because they come from a different frame and change at a
 * different rate — the layout is per zone and the entities are per tick — so a component
 * can memoise one without recomputing the other thirty times a second.
 */
export function landmarkBlips(zone: ZonePayload, box: MapBox): Blip[] {
  const out: Blip[] = [
    { id: 'entry', kind: 'entry', at: projectToMap(zone, box, zone.spawn) },
  ];
  // Town's "exit" is the [ GRIND ] gate, which is a portal rather than a way down — and
  // it already has its own marker in the world. Marking it as an exit here would promise
  // a descent that town does not have.
  if (zone.kind !== 'HUB') {
    out.push({ id: 'exit', kind: 'exit', at: projectToMap(zone, box, zone.exit) });
  }
  return out;
}
